import { ExclamationCircleIcon } from '@heroicons/react/solid';
import { Button, ButtonVariantEnum, Modal } from '@Wonder-Cave/ui';
import { IPurchaseProviderResult, IPurchaseReviewSummary } from './PurchaseNumbersModal';

interface IPurchaseNumbersReviewModalProps {
  show: boolean;
  summary: IPurchaseReviewSummary | null;
  isPostPurchase: boolean;
  onBack: () => void;
}

const PurchaseNumbersReviewModal = ({
  show,
  summary,
  isPostPurchase,
  onBack,
}: IPurchaseNumbersReviewModalProps) => {
  if (!summary) {
    return null;
  }

  const hasPurchaseErrors = summary.results.some(r => r.purchaseError != null);
  const hasSearchErrors = summary.results.some(r => r.error !== null);
  const hasAnyErrors = isPostPurchase ? hasPurchaseErrors : hasSearchErrors || hasPurchaseErrors;

  const totalSelected = summary.results
    .filter(r => !r.purchaseError)
    .reduce((sum, r) => sum + r.phoneNumbers.length, 0);

  const titleComponent = (
    <div className='flex flex-col mt-4 mb-4'>
      <h2 className='w-full'>Review Purchase</h2>
      <div className='pt-2 text-regular font-medium text-dark-gray'>
        {totalSelected} {totalSelected === 1 ? 'number' : 'numbers'} selected:
      </div>
    </div>
  );

  return (
    <Modal
      title={titleComponent}
      show={show}
      onClose={onBack}
      titleClassName='pl-20 text-black'
      childrenClassName='modal-panel-grey pl-20 pr-20 pb-2 pt-2'
    >
      <div className='flex flex-col w-full'>
        <div className='grid grid-cols-[24px_2fr_2fr_3fr_3fr] gap-x-4 pb-3 border-b border-mystic text-h5 font-medium tracking-widest uppercase text-medium-gray'>
          <span />
          <span>Quantity</span>
          <span>Area Code</span>
          <span>TCR Campaign ID</span>
          <span>Provider</span>
        </div>

        <div className='flex flex-col max-h-96 overflow-y-auto'>
          {summary.results.map((result: IPurchaseProviderResult, idx: number) => {
            const rowError = isPostPurchase ? result.purchaseError ?? null : result.error ?? result.purchaseError ?? null;
            const hasRowIssue = rowError !== null;
            const tooltip = rowError ?? undefined;
            return (
              <div
                key={`${result.tcrCampaignId}-${result.providerName}-${idx}`}
                className='grid grid-cols-[24px_2fr_2fr_3fr_3fr] gap-x-4 items-center py-3 border-b border-mystic text-sm text-dark-gray'
                title={tooltip}
              >
                <span className='flex items-center justify-center'>
                  {hasRowIssue && (
                    <ExclamationCircleIcon className='w-3 h-3 text-flamingo' />
                  )}
                </span>
                <span>{result.phoneNumbers.length === 0 ? 'N/A' : result.phoneNumbers.length}</span>
                <span>{summary.areaCode}</span>
                <span
                  className='overflow-hidden text-ellipsis whitespace-nowrap'
                  title={result.tcrCampaignId}
                >
                  {result.tcrCampaignId}
                </span>
                <span>{result.providerName}</span>
              </div>
            );
          })}
        </div>

        {hasAnyErrors && (
          <div className='pt-4 pb-2'>
            <span className='text-xs font-bold text-flamingo'>
              {summary.results.filter(r => isPostPurchase ? r.purchaseError != null : r.error !== null || r.purchaseError != null).length}{' '}
              {summary.results.filter(r => isPostPurchase ? r.purchaseError != null : r.error !== null || r.purchaseError != null).length === 1 ? 'Error' : 'Errors'} Detected:
            </span>
            <ol className='list-decimal list-inside flex flex-col gap-1 mt-1 max-h-32 overflow-y-auto'>
              {summary.results
                .filter(r => isPostPurchase ? r.purchaseError != null : r.error !== null || r.purchaseError != null)
                .map((r, idx) => (
                  <li key={idx} className='text-sm font-bold text-black'>
                    {r.purchaseError ?? r.error}
                  </li>
                ))}
            </ol>
          </div>
        )}

        <div className='flex flex-row items-center justify-end pt-6 pb-4'>
          {isPostPurchase && !hasAnyErrors && (
            <span className='text-xs font-bold text-black mr-auto'>No issues detected.</span>
          )}
          <Button variant={ButtonVariantEnum.SECONDARY} type='button' onClick={onBack}>
            {isPostPurchase ? 'CLOSE' : 'BACK TO EDIT'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PurchaseNumbersReviewModal;
